import React, { useEffect, useMemo, useState } from 'react';
import PageMeta from '../components/PageMeta';
import { Button } from '../components/ui/button';
import { getApiUrl } from '../config/api';

const Pricing = () => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const plan = new URLSearchParams(window.location.search).get('plan');
    return plan === 'premium' ? 'premium' : 'basic';
  });

  const plans = useMemo(() => ([
    {
      code: 'basic',
      name: 'Basic',
      price: '₹299 / month',
      description: 'For serious learners who want consistent coverage.',
      features: [
        'Full access to basic research reports',
        'Weekly market summary',
        'Email support'
      ],
    },
    {
      code: 'premium',
      name: 'Premium',
      price: '₹999 / month',
      description: 'For professionals who need deep, timely insights.',
      features: [
        'Everything in Basic',
        'Premium reports & sector deep dives',
        'Priority support'
      ],
    },
  ]), []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoadingUser(true);
        const API = getApiUrl();
        const resp = await fetch(`${API}/auth/me`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          setUser(data);
        } else {
          setUser(null);
        }
      } catch (e) {
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, []);

  const selected = plans.find((p) => p.code === selectedPlan) || plans[0];

  return (
    <>
      <PageMeta
        title="Pricing & Subscription | The B Tree"
        description="Compare basic and premium subscription options for equity, sector, legal, and public policy intelligence from The B Tree."
        canonical="https://www.thebtree.com/pricing"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <div className="min-h-screen bg-[#F7F5F2]">
      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-[Cormorant_Garamond] font-medium text-slate-900 mb-2">
            Pricing & Subscription
          </h1>
          <p className="text-slate-600">
            Select a plan and proceed to payment (Razorpay integration will be enabled next).
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500">Signed in as</div>
              {loadingUser ? (
                <div className="mt-2 text-slate-700">Loading…</div>
              ) : user ? (
                <div className="mt-2 text-slate-900 font-medium">
                  {user.name || 'User'}
                  <span className="text-slate-500 font-normal"> · {user.email}</span>
                </div>
              ) : (
                <div className="mt-2 text-slate-700">
                  Not signed in. Please sign in before subscribing.
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="border-slate-200"
                onClick={() => (window.location.href = '/equity-intelligence')}
              >
                Back to Equity Intelligence™
              </Button>
              {!user && (
                <Button
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-semibold"
                  onClick={() => (window.location.href = '/signin')}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {plans.map((plan) => {
              const isSelected = plan.code === selectedPlan;
              return (
                <button
                  key={plan.code}
                  type="button"
                  onClick={() => setSelectedPlan(plan.code)}
                  className={`text-left bg-white border rounded-2xl p-6 transition-all duration-300 ${
                    isSelected
                      ? 'border-[#D4AF37] shadow-lg'
                      : 'border-slate-200 hover:border-[#D4AF37] hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-2xl font-[Cormorant_Garamond] font-medium text-slate-900">
                        {plan.name}
                      </div>
                      <div className="mt-1 text-slate-600">{plan.description}</div>
                    </div>
                    <div className={`w-4 h-4 rounded-full border ${isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-slate-300'}`} />
                  </div>

                  <div className="mt-4 text-lg font-semibold text-slate-900">{plan.price}</div>

                  <div className="mt-4">
                    <div className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500">Includes</div>
                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#C8A84B]" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 h-fit">
            <div className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500">Selected plan</div>
            <div className="mt-2 text-2xl font-[Cormorant_Garamond] font-medium text-slate-900">
              {selected.name}
            </div>
            <div className="mt-1 text-slate-700 font-semibold">{selected.price}</div>

            <div className="mt-6">
              <div className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500">Payment (Razorpay)</div>
              <div className="mt-2 text-sm text-slate-600">
                Payment is not wired yet. Next step will be:
              </div>
              <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal list-inside">
                <li>Click “Proceed to payment”</li>
                <li>We create a Razorpay order on the backend</li>
                <li>Razorpay Checkout opens and you complete payment</li>
              </ol>
            </div>

            <div className="mt-6 space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-semibold"
                disabled={!user}
                onClick={() => {
                  // Placeholder: no API calls yet
                  window.location.href = `/pricing?plan=${encodeURIComponent(selectedPlan)}&pay=razorpay`;
                }}
              >
                Proceed to payment
              </Button>

              <Button
                variant="outline"
                className="w-full border-slate-200"
                onClick={() => (window.location.href = '/signin')}
                disabled={!!user}
              >
                Sign in to continue
              </Button>

              <div className="text-xs text-slate-500">
                Payment links will be generated per-user and per-plan.
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    </>
  );
};

export default Pricing;
